import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InputNumber } from "antd";
import { toast } from "react-toastify";
import { getApi } from "../../../Services/apiService";
import { hasAuthToken, PENDING_SUBSCRIPTION_ONBOARDING_KEY } from "../../../Utils/authStorage";
import {
  ensurePlanLabel,
  getLoggedInUserRole,
  getStoredFamilyTreeId,
  getSubscriptionPlansApiUrl,
  isTruthySubscriptionFlag,
  shouldFetchSubscriptionPlansWithFamilyTreeId,
} from "../../../Utils/subscriptionAccess";
import SubscriptionCheckoutFlow from "../../Shared/SubscriptionCheckoutFlow";
import PlanCardPromoBadges from "../../Shared/PlanCardPromoBadges";
import "../../../Auth.css";

/** Same rule as login redirect: roles 1 & 2 → user dashboard */
function getDashboardPathFromUserDetail() {
  try {
    const ud = JSON.parse(localStorage.getItem("userDetail") || "{}");
    if (ud?.role === "1" || ud?.role === "2") return "/user/dashboard";
  } catch (_) {
    /* ignore */
  }
  return "/dashboard";
}

const BILLING_CYCLE_LABELS = {
  1: "Monthly",
  2: "3-Month",
  3: "Yearly",
  4: "Lifetime",
};

const PLAN_TYPE_LABELS = {
  1: "Mother's Side",
  2: "Father's Side",
  3: "Both Sides",
  4: "Lifetime (Mother's side)",
  6: "Member line",
  7: "Lifetime (Father's side)",
};

/** The "Both Sides" plan reads the same on its card and in the active hint */
const BOTH_SIDES_CARD_TITLE = "Both Sides Yearly Plan";
const BOTH_SIDES_CARD_NOTE = "(Mother & Father)";

/** Recurring buckets first, then lifetime (mother + father promos in one array from API). */
const PLAN_SECTIONS = [
  { key: "mothers_side", title: PLAN_TYPE_LABELS[1], group: "recurring" },
  { key: "fathers_side", title: PLAN_TYPE_LABELS[2], group: "recurring" },
  { key: "both_sides", title: "", group: "recurring", hideTitle: true },
  { key: "lifetime", title: "", group: "lifetime", hideTitle: true },
  /** Legacy buckets if API still splits promos */
  { key: "lifetime_mother_promo", title: PLAN_TYPE_LABELS[4], group: "lifetime" },
  { key: "lifetime_father_promo", title: PLAN_TYPE_LABELS[7], group: "lifetime" },
  { key: "lifetime_promo", title: "", group: "lifetime", hideTitle: true },
];

const PLAN_GROUPS = [
  { key: "recurring", title: "Recurring Plans" },
  { key: "lifetime", title: "Lifetime Plans" },
];

/** Shared with the hidden width gauge that sizes a lone lifetime card to this line. */
const INTRO_RATE_END_TEXT = "Introductory rates end December 31, 2026";

/** GET /subscription/plans?family_tree_id=… — member buckets from API */
const MEMBER_PLAN_SECTIONS = [
  { key: "member_basic", title: "Member basic" },
  { key: "member_standard", title: "Yearly Admin Fee" },
];

/** plan_type on API / role lines up with section bucket */
const SECTION_KEY_TO_PLAN_TYPE = {
  mothers_side: 1,
  fathers_side: 2,
  both_sides: 3,
  lifetime_mother_promo: 4,
  lifetime_father_promo: 7,
  /** Legacy bucket if API still returns it */
  lifetime_promo: 4,
  member_basic: 6,
};

function planTypeBillingKey(plan) {
  return `${Number(plan?.plan_type)}:${String(plan?.billing_cycle ?? "").trim()}`;
}

function findActiveSubscriptionEntry(plan, activeSubscriptions) {
  if (!Array.isArray(activeSubscriptions) || activeSubscriptions.length === 0) return null;
  const k = planTypeBillingKey(plan);
  return (
    activeSubscriptions.find(
      (a) => `${Number(a.plan_type)}:${String(a.billing_cycle ?? "").trim()}` === k
    ) ?? null
  );
}

function planMatchesActiveSubscription(plan, activeSubscriptions) {
  return findActiveSubscriptionEntry(plan, activeSubscriptions) != null;
}

/** Per-plan flag from GET /subscription/plans (when active_subscriptions array is absent or redundant). */
function planHasActiveSubscriptionFlag(plan) {
  const v = plan?.active_subscription ?? plan?.activeSubscription;
  return isTruthySubscriptionFlag(v);
}

function isPlanActivatedForCard(plan, activeSubscriptions) {
  return planMatchesActiveSubscription(plan, activeSubscriptions) || planHasActiveSubscriptionFlag(plan);
}

/** Use amount/currency from active_subscriptions when plan row omits them (e.g. snapshot from role). */
function enrichPlansWithActiveSubscriptionPricing(items, activeSubscriptions) {
  if (!Array.isArray(items) || !Array.isArray(activeSubscriptions) || activeSubscriptions.length === 0) {
    return items;
  }
  return items.map((plan) => {
    const act = findActiveSubscriptionEntry(plan, activeSubscriptions);
    if (!act) return plan;
    const amount = plan.amount ?? plan.price ?? act.amount;
    const currency = (plan.currency || act.currency || "usd").toString();
    return { ...plan, amount, currency };
  });
}

function subscriptionMatchesSectionPlanType(sectionKey, planType) {
  const t = Number(planType);
  if (sectionKey === "lifetime") return t === 4 || t === 7;
  const expected = SECTION_KEY_TO_PLAN_TYPE[sectionKey];
  return expected != null && t === Number(expected);
}

/**
 * If the current plan is active but omitted from GET /subscription/plans list, add it from loggedIn_user_role.subscription.Plan.
 * Put active plan first in the row.
 */
function injectAndOrderActivePlan(sectionKey, items, activeSubscriptions) {
  const list = Array.isArray(items) ? [...items] : [];
  const subs = Array.isArray(activeSubscriptions) ? activeSubscriptions : [];
  const expectedType = SECTION_KEY_TO_PLAN_TYPE[sectionKey];
  const canInjectFromSubs =
    subs.length > 0 && (sectionKey === "lifetime" || expectedType != null);

  if (canInjectFromSubs) {
    const keys = new Set(list.map((p) => planTypeBillingKey(p)));

    for (const act of subs) {
      if (!subscriptionMatchesSectionPlanType(sectionKey, act.plan_type)) continue;
      const k = `${Number(act.plan_type)}:${String(act.billing_cycle ?? "").trim()}`;
      if (keys.has(k)) continue;

      const p = getLoggedInUserRole()?.subscription?.Plan;
      if (
        p &&
        Number(p.plan_type) === Number(act.plan_type) &&
        String(p.billing_cycle ?? "").trim() === String(act.billing_cycle ?? "").trim()
      ) {
        list.unshift({
          ...p,
          amount: p.amount ?? p.price ?? act.amount,
          currency: p.currency || act.currency || "usd",
        });
        keys.add(k);
      }
    }
  }

  const sorted = list.sort((a, b) => {
    const aAct = isPlanActivatedForCard(a, subs);
    const bAct = isPlanActivatedForCard(b, subs);
    if (aAct && !bAct) return -1;
    if (!aAct && bAct) return 1;
    return 0;
  });
  return subs.length > 0
    ? enrichPlansWithActiveSubscriptionPricing(sorted, subs)
    : sorted;
}

function billingCycleLabel(plan) {
  const raw = plan?.billing_cycle;
  if (raw === undefined || raw === null || raw === "") return "";
  const key = Number(String(raw).trim());
  if (Number.isFinite(key) && BILLING_CYCLE_LABELS[key]) {
    return BILLING_CYCLE_LABELS[key];
  }
  return "";
}

function isYearlyPlan(plan) {
  const raw = plan?.billing_cycle;
  if (raw === undefined || raw === null || raw === "") return false;
  return Number(String(raw).trim()) === 3;
}

function planTypeLabel(planType) {
  if (planType === undefined || planType === null || planType === "") return "";
  const key = Number(planType);
  if (Number.isFinite(key) && PLAN_TYPE_LABELS[key]) {
    return PLAN_TYPE_LABELS[key];
  }
  return "";
}

function subscriptionLikeToDisplayLine(row) {
  if (!row || typeof row !== "object") return "";
  // Reads the same as its card: "Both Sides Yearly Plan (Mother & Father)"
  if (Number(row.plan_type) === 3) return `${BOTH_SIDES_CARD_TITLE} ${BOTH_SIDES_CARD_NOTE}`;
  const pt = planTypeLabel(row.plan_type);
  const nameHint = (row.name && String(row.name).trim()) || "";
  const bcKey = Number(String(row.billing_cycle ?? "").trim());
  const bc =
    Number.isFinite(bcKey) && BILLING_CYCLE_LABELS[bcKey] ? BILLING_CYCLE_LABELS[bcKey] : "";
  const typePart = pt || nameHint.replace(/_/g, " ");
  const typeLower = typePart.toLowerCase();
  const billingLower = bc.toLowerCase();
  if (typePart && billingLower && typeLower.includes(billingLower)) return ensurePlanLabel(typePart);
  if (typePart && bc) return ensurePlanLabel(`${typePart} - ${bc}`);
  if (typePart) return ensurePlanLabel(typePart);
  if (bc) return ensurePlanLabel(bc);
  return "";
}

/** Omit lifetime lines from the "Active on your account" hint (lifetime cards still show Activated). */
function activeDisplayLinesWithoutLifetime(lines) {
  if (!Array.isArray(lines)) return [];
  return lines.filter((line) => !/\blifetime\b/i.test(String(line)));
}

/** When API omits data.active_subscriptions but sets plan.active_subscription on rows. */
function activeDisplayLinesFromPlanBuckets(plans) {
  if (!plans || typeof plans !== "object") return [];
  const lines = [];
  const seen = new Set();
  for (const arr of Object.values(plans)) {
    if (!Array.isArray(arr)) continue;
    for (const p of arr) {
      if (!planHasActiveSubscriptionFlag(p)) continue;
      const line = subscriptionLikeToDisplayLine(p);
      if (line && !seen.has(line)) {
        seen.add(line);
        lines.push(line);
      }
    }
  }
  return lines;
}

/** GET /subscription/plans — data.active_subscriptions + data.plans */
function normalizeUserPlansResponse(res) {
  const data = res?.data != null && typeof res.data === "object" ? res.data : res;
  if (!data || typeof data !== "object") {
    return { plans: {}, activeDisplayLines: [], activeSubscriptions: [] };
  }
  const plans = typeof data.plans === "object" && data.plans ? data.plans : {};
  const subs = Array.isArray(data.active_subscriptions) ? data.active_subscriptions : [];
  let activeDisplayLines = subs.map((s) => subscriptionLikeToDisplayLine(s)).filter(Boolean);
  if (activeDisplayLines.length === 0) {
    activeDisplayLines = activeDisplayLinesFromPlanBuckets(plans);
  }
  activeDisplayLines = activeDisplayLinesWithoutLifetime(activeDisplayLines);
  return { plans, activeDisplayLines, activeSubscriptions: subs };
}

function formatMoney(value, currency = "USD") {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

/** Member-line active subscription (plan_type 6), else the first active entry. */
function getActiveMemberSubscription(activeSubscriptions) {
  if (!Array.isArray(activeSubscriptions) || activeSubscriptions.length === 0) return null;
  return activeSubscriptions.find((a) => Number(a?.plan_type) === 6) || activeSubscriptions[0];
}

/** GET /subscription/plans can carry the dates on the plan row itself (active_subscription +
    start_date/end_date) with active_subscriptions empty — that row is the renewal-date source then. */
function getActiveMemberPlanRow(sections) {
  if (!Array.isArray(sections)) return null;
  for (const section of sections) {
    if (section?.key !== "member_standard" && section?.key !== "member_basic") continue;
    const hit = (section.items || []).find((plan) => planHasActiveSubscriptionFlag(plan));
    if (hit) return hit;
  }
  return null;
}

function toDate(raw) {
  if (raw == null || raw === "") return null;
  // The API sends epoch timestamps as bare numbers or as numeric strings ("1816799400000"),
  // and new Date("1816799400000") is Invalid Date — coerce those before falling back to Date parsing.
  const epoch =
    typeof raw === "number"
      ? raw
      : typeof raw === "string" && /^\d+$/.test(raw.trim())
      ? Number(raw.trim())
      : null;
  const d = epoch != null ? new Date(epoch < 1e12 ? epoch * 1000 : epoch) : new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Renewal / next-payment date from whichever field the API sends. */
function readSubscriptionRenewalDate(entry) {
  if (!entry || typeof entry !== "object") return null;
  return toDate(
    entry.next_payment_date ??
      entry.next_billing_date ??
      entry.next_payment ??
      entry.renewal_date ??
      entry.current_period_end ??
      entry.period_end ??
      entry.end_date ??
      entry.expiry_date ??
      entry.expires_at ??
      entry.valid_till ??
      null
  );
}

/** Activation / start date from whichever field the API sends. */
function readSubscriptionStartDate(entry) {
  if (!entry || typeof entry !== "object") return null;
  return toDate(
    entry.start_date ??
      entry.started_at ??
      entry.activated_at ??
      entry.current_period_start ??
      entry.created_at ??
      null
  );
}

/** M/D/YY */
function formatShortDate(d) {
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
}

function formatPlanDisplayName(plan) {
  const raw = (plan?.name && String(plan.name).trim()) || "";
  if (!raw) return "";
  const spaced = raw.replace(/_/g, " ");
  const titled = spaced.replace(/\b\w/g, (c) => c.toUpperCase());
  // Title-case treats ’ or ' as a word break, producing Mother’S; normalize possessive ’s / 's.
  return titled.replace(/(['\u2019])S(?=\s|$)/g, "$1s");
}

/** Lifetime grid: both-parent combined card — match SubscriptionPlansPicker (API name may use promo or plan) */
function isMotherFatherSidesPromoPlan(plan) {
  if (Number(plan?.plan_type) === 8) return true;
  const slug = String(plan?.name ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (
    slug.includes("mother_father_sides") &&
    (slug.includes("promo") || slug.includes("_plan") || slug.endsWith("plan"))
  ) {
    return true;
  }
  const display = formatPlanDisplayName(plan).toLowerCase();
  return (
    display.includes("mother") &&
    display.includes("father") &&
    display.includes("sides") &&
    (display.includes("promo") || display.includes("plan"))
  );
}

/** Lifetime cards read as "<Side> Lifetime Plan" (e.g. "Mother's Side Lifetime Plan"). */
function withLifetimePlanSuffix(side) {
  const text = String(side || "").trim();
  if (!text) return "";
  return /\blifetime\b/i.test(text) ? ensurePlanLabel(text) : ensurePlanLabel(`${text} Lifetime`);
}

function lifetimeCardTitle(plan) {
  if (isMotherFatherSidesPromoPlan(plan)) return withLifetimePlanSuffix("Both Sides");
  const planType = Number(plan?.plan_type);
  if (planType === 4) return withLifetimePlanSuffix("Mother's Side");
  if (planType === 7) return withLifetimePlanSuffix("Father's Side");
  const display = formatPlanDisplayName(plan).toLowerCase();
  if (display.includes("mother") && !display.includes("father")) {
    return withLifetimePlanSuffix("Mother's Side");
  }
  if (display.includes("father") && !display.includes("mother")) {
    return withLifetimePlanSuffix("Father's Side");
  }
  const fallback = String(formatPlanDisplayName(plan) || plan.name || "")
    .replace(/\bPromo\b/gi, "")
    .replace(/\bPlan\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  return withLifetimePlanSuffix(fallback) || "Lifetime Plan";
}

function getPlanDisplayAmount(plan, act) {
  if (plan?.is_promo_active && plan?.promo_amount != null && plan.promo_amount !== "") {
    return plan.promo_amount;
  }
  if (plan?.amount != null && plan.amount !== "") return plan.amount;
  if (plan?.price != null && plan.price !== "") return plan.price;
  return act?.amount;
}

function PlanCard({ plan, sectionKey, onSelect, isActive, activeSubscriptions }) {
  const act = isActive ? findActiveSubscriptionEntry(plan, activeSubscriptions) : null;
  const displayAmount = getPlanDisplayAmount(plan, act);
  const currency = (plan.currency || act?.currency || "USD").toString();
  const cycleLabel = billingCycleLabel(plan);
  const nameLabel = formatPlanDisplayName(plan);
  const isLifetimeSection =
    sectionKey === "lifetime" ||
    sectionKey === "lifetime_mother_promo" ||
    sectionKey === "lifetime_father_promo" ||
    sectionKey === "lifetime_promo";
  const isBothSidesSection = sectionKey === "both_sides";
  const isMemberPlanSection = sectionKey === "member_basic" || sectionKey === "member_standard";

  let titleText;
  const isMemberStandard = sectionKey === "member_standard";
  if (isMemberStandard) {
    titleText = "Annual Household Admin Fee";
  } else if (sectionKey === "member_basic") {
    titleText = [nameLabel, cycleLabel].filter(Boolean).join(" — ") || nameLabel || cycleLabel || "Plan";
  } else if (isBothSidesSection) {
    titleText = BOTH_SIDES_CARD_TITLE;
  } else if (isLifetimeSection) {
    titleText = lifetimeCardTitle(plan);
  } else {
    titleText = ensurePlanLabel(cycleLabel || plan.name || "Plan");
  }

  const featureEntries =
    plan.features && typeof plan.features === "object" && !Array.isArray(plan.features)
      ? Object.entries(plan.features).filter(([, v]) => v != null && v !== "")
      : [];

  const showBestValue =
    !isLifetimeSection && !isBothSidesSection && isYearlyPlan(plan) && !isMemberPlanSection;
  const showBestSavings = isLifetimeSection && isMotherFatherSidesPromoPlan(plan);
  const showMostPopular = isBothSidesSection;
  const hasCornerBalloon = showBestValue || showBestSavings || showMostPopular;

  return (
    <div
      className={`signup-plan-card-wrap${
        hasCornerBalloon ? " signup-plan-card-wrap--corner-balloon" : ""
      }${isBothSidesSection ? " signup-plan-card-wrap--wide" : ""}`}
    >
      {hasCornerBalloon ? (
        <PlanCardPromoBadges
          showBestValue={showBestValue}
          showBestSavings={showBestSavings}
          showMostPopular={showMostPopular}
          placement="corner"
        />
      ) : null}
      <div
        className={`signup-plan-card${isActive ? " signup-plan-card--active-subscription" : ""}`}
      >
        <h3 className={`signup-plan-card-title${isMemberStandard ? " signup-plan-card-title--member-standard" : ""}`}>
          <span className="signup-plan-card-title-main">{titleText}</span>
          {isMemberStandard ? (
            <em className="signup-plan-card-title-note">(Limited to 2 users at a time, per session)</em>
          ) : null}
          {isBothSidesSection ? (
            <em className="signup-plan-card-title-note signup-plan-card-title-note--both-sides">
              {BOTH_SIDES_CARD_NOTE}
            </em>
          ) : null}
        </h3>
        {plan.description ? <p className="signup-plan-card-desc">{plan.description}</p> : null}
        <div className="signup-plan-card-price">
          {formatMoney(displayAmount, currency.toUpperCase())}
        </div>
        {featureEntries.length > 0 ? (
          <ul className="signup-plan-card-features">
            {featureEntries.map(([k, v]) => (
              <li key={k}>
                <strong>{k}:</strong> {String(v)}
              </li>
            ))}
          </ul>
        ) : null}
        {isActive ? (
          <div className="signup-plan-card-activated" role="status">
            Activated
          </div>
        ) : (
          <button type="button" className="signup-plan-card-btn" onClick={() => onSelect(plan, sectionKey)}>
            {isMemberPlanSection
              ? sectionKey === "member_standard"
                ? "Pay Yearly Fee"
                : "Pay Admin Fee"
              : "Make Payment"}
          </button>
        )}
      </div>
    </div>
  );
}

function DonationCard({ onDonate }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [amount, setAmount] = useState(null);

  const handleContinue = () => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Please enter a donation amount greater than $0.");
      return;
    }
    onDonate(n);
  };

  return (
    <div
      className={`signup-plan-card-wrap subscription-donation-card-wrap${
        panelOpen ? " subscription-donation-card-wrap--expanded" : ""
      }`}
    >
      <div className="signup-plan-card subscription-donation-card">
        <h3 className="signup-plan-card-title subscription-donation-title">Donation</h3>
        <p className="signup-plan-card-desc subscription-donation-desc">
          If you would like to give a donation in addition to your annual fee or in lieu of your fee, your
          contribution will help fund further improvements and keep this app active for
          years to come.
        </p>
        {!panelOpen ? (
          <button
            type="button"
            className="signup-plan-card-btn"
            onClick={() => setPanelOpen(true)}
          >
            Your Donation
          </button>
        ) : (
          <div className="subscription-donation-panel">
            <div className="subscription-donation-field">
              <label className="subscription-donation-label" htmlFor="donation-amount">
                Amount (USD)
              </label>
              <InputNumber
                id="donation-amount"
                className="subscription-donation-input"
                min={0.01}
                step={1}
                precision={2}
                prefix="$"
                placeholder="0.00"
                value={amount}
                onChange={setAmount}
                controls={false}
              />
            </div>
            <div className="subscription-donation-actions">
              <button
                type="button"
                className="signup-plan-card-btn subscription-donation-payment-btn"
                onClick={handleContinue}
              >
                Payment
              </button>
              <button
                type="button"
                className="subscription-donation-cancel-btn"
                onClick={() => {
                  setPanelOpen(false);
                  setAmount(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState({
    plans: {},
    activeDisplayLines: [],
    activeSubscriptions: [],
  });
  const [checkout, setCheckout] = useState(null);

  const fetchPlans = useCallback(async () => {
    if (!hasAuthToken()) return;
    setLoading(true);
    setError("");
    try {
      const res = await getApi(getSubscriptionPlansApiUrl());
      setPayload(normalizeUserPlansResponse(res));
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Could not load plans.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAuthToken()) fetchPlans();
  }, [fetchPlans]);

  const isMemberPlans = shouldFetchSubscriptionPlansWithFamilyTreeId();

  const sectionsWithPlans = useMemo(() => {
    const active = payload.activeSubscriptions || [];
    const sectionDefs = isMemberPlans ? MEMBER_PLAN_SECTIONS : PLAN_SECTIONS;
    return sectionDefs
      .map((section) => {
        const raw = Array.isArray(payload.plans[section.key]) ? payload.plans[section.key] : [];
        return {
          ...section,
          items: injectAndOrderActivePlan(section.key, raw, active),
        };
      })
      .filter((s) => s.items.length > 0);
  }, [payload.plans, payload.activeSubscriptions, isMemberPlans]);

  const planGroups = useMemo(() => {
    if (isMemberPlans) return null;
    return PLAN_GROUPS.map((group) => ({
      ...group,
      sections: sectionsWithPlans.filter((s) => s.group === group.key),
    })).filter((g) => g.sections.length > 0);
  }, [sectionsWithPlans, isMemberPlans]);

  const totalCards = useMemo(
    () => sectionsWithPlans.reduce((n, s) => n + s.items.length, 0),
    [sectionsWithPlans]
  );

  /** Widest row wins: every grid uses this column count, so all cards share one width. */
  const planColumnCount = useMemo(() => {
    if (!planGroups) return 3;
    let widest = 0;
    planGroups.forEach((group) => {
      group.sections.forEach((section) => {
        widest = Math.max(widest, section.items.length);
      });
    });
    return Math.max(3, widest);
  }, [planGroups]);

  const handleSelectPlan = (plan, sectionKey) => {
    if (!plan) return;
    setCheckout({ mode: "plan", plan, planCategory: sectionKey });
  };

  const handleStartDonation = (amount) => {
    setCheckout({ mode: "donation", amount });
  };

  const handlePaymentSuccess = () => {
    localStorage.removeItem(PENDING_SUBSCRIPTION_ONBOARDING_KEY);
    toast.success("Subscription active! Welcome to Fam Connect.");
    setCheckout(null);
    navigate("/dashboard");
  };

  const handleDonationSuccess = () => {
    toast.success("Thank you for your donation!");
    setCheckout(null);
  };

  const isMemberLineCheckout = shouldFetchSubscriptionPlansWithFamilyTreeId();
  const familyTreeIdForIntent = isMemberLineCheckout ? getStoredFamilyTreeId() : "";

  /** Something is already active on this account, so the picker collapses to the
      plan(s) that still apply — a short stack that reads better centred. The
      backend reports this either as the top-level active_subscriptions array or
      as a per-plan flag, so a card can read "Activated" with the array empty —
      check both, or the collapsed stack stays left justified. */
  const hasActiveSubscription =
    (payload.activeSubscriptions?.length ?? 0) > 0 ||
    sectionsWithPlans.some((section) =>
      section.items.some((plan) => planHasActiveSubscriptionFlag(plan))
    );

  const activeMemberEntry = getActiveMemberSubscription(payload.activeSubscriptions);
  const activeMemberPlanRow = getActiveMemberPlanRow(sectionsWithPlans);
  const hasActiveMemberPlan =
    isMemberLineCheckout &&
    (!!activeMemberEntry || (payload.activeDisplayLines?.length ?? 0) > 0);
  const now = new Date();
  const memberStartDate =
    readSubscriptionStartDate(activeMemberEntry) || readSubscriptionStartDate(activeMemberPlanRow);
  const memberActiveYear = (memberStartDate || now).getFullYear();
  const memberRenewalDate =
    readSubscriptionRenewalDate(activeMemberEntry) ||
    readSubscriptionRenewalDate(activeMemberPlanRow) ||
    (() => {
      const base = memberStartDate || now;
      return new Date(base.getFullYear() + 1, base.getMonth(), base.getDate());
    })();
  const memberNextPaymentText = formatShortDate(memberRenewalDate);

  return (
    <div
      className={`subscription-management change-password right-with-header${
        totalCards > 1 ? " subscription-management--wide-plans" : ""
      }`}
    >
      <div className="main-rightside">
        {checkout ? (
          checkout.mode === "donation" ? (
            <SubscriptionCheckoutFlow
              key={`donation-${checkout.amount}`}
              checkoutType="donation"
              donationAmount={checkout.amount}
              familyTreeIdForIntent={familyTreeIdForIntent || null}
              onPaymentSuccess={handleDonationSuccess}
              wrapperClassName="settings-embedded-checkout"
              headerSlot={
                <div className="settings-checkout-back settings-subscription-back">
                  <button
                    type="button"
                    className="settings-checkout-back-btn"
                    onClick={() => setCheckout(null)}
                  >
                    ← Back to Yearly Admin Fee
                  </button>
                </div>
              }
            />
          ) : (
            <SubscriptionCheckoutFlow
              key={`${checkout.plan.id}-${checkout.planCategory}`}
              selectedPlan={checkout.plan}
              planCategory={checkout.planCategory}
              familyTreeIdForIntent={familyTreeIdForIntent || null}
              onPaymentSuccess={handlePaymentSuccess}
              wrapperClassName="settings-embedded-checkout"
              headerSlot={
                <div className="settings-checkout-back settings-subscription-back">
                  <button
                    type="button"
                    className="settings-checkout-back-btn"
                    onClick={() => setCheckout(null)}
                  >
                    {isMemberLineCheckout ? "← Back to Yearly Admin Fee" : "← Back to plans"}
                  </button>
                </div>
              }
            />
          )
        ) : (
          <div className="settings-subscription-plans-shell">
            <div className="settings-checkout-back settings-subscription-back">
              <button
                type="button"
                className="settings-checkout-back-btn"
                onClick={() => navigate(getDashboardPathFromUserDetail())}
              >
                ← Back to Dashboard
              </button>
            </div>
            <div
              className={`settings-plans-inner${isMemberLineCheckout ? " settings-plans-inner--member-line" : ""}${
                hasActiveSubscription ? " settings-plans-inner--activated" : ""
              }`}
            >
              <div className="signup-plans-header settings-plans-header">
                {!isMemberLineCheckout ? (
                  <>
                    <h3 className="signup-plans-title">Recurring or Lifetime Subscription Plans</h3>
                    <p className="signup-plans-urgency">
                      HURRY and SIGNUP to lock in this special INTRODUCTORY rate for a year!
                    </p>
                    <p className="signup-plans-disclaimer">{INTRO_RATE_END_TEXT}</p>
                  </>
                ) : null}
                {isMemberLineCheckout ? (
                  hasActiveMemberPlan ? (
                    <p className="signup-plans-active-hint signup-plans-active-hint--member-line">
                      Active on your account:
                      <strong className="subscription-active-hint-value">
                        Household Admin Fee -{" "}
                        <span className="subscription-emphasis">{memberActiveYear}</span>
                      </strong>
                    </p>
                  ) : null
                ) : payload.activeDisplayLines?.length > 0 ? (
                  <p className="signup-plans-active-hint">
                    Active on your account: {payload.activeDisplayLines.join(", ")}
                  </p>
                ) : null}
              </div>

              {loading ? (
                <div className="signup-plans-loading">Loading plans…</div>
              ) : error ? (
                <div className="signup-plans-error-block" role="alert">
                  <div className="signup-payment-error-banner">{error}</div>
                  <p className="signup-plans-error-hint">You must load plans and pick one to continue.</p>
                  <button type="button" className="signup-plans-skip-btn" onClick={() => fetchPlans()}>
                    Try again
                  </button>
                </div>
              ) : totalCards === 0 ? (
                <div className="signup-plans-empty">
                  <p>No plans are available right now. Try refreshing.</p>
                  <button type="button" className="signup-plans-skip-btn" onClick={() => fetchPlans()}>
                    Try again
                  </button>
                </div>
              ) : isMemberLineCheckout ? (
                <div className="signup-plans-sections settings-subscription-plan-sections">
                  {sectionsWithPlans.map((section) => {
                    const visiblePlans = section.items;
                    const showDonation = section.key === "member_standard";
                    if (visiblePlans.length === 0 && !showDonation) return null;

                    return (
                      <section key={section.key} className="signup-plans-section">
                        <div
                          className={`signup-plans-grid${
                            showDonation && visiblePlans.length > 0
                              ? " signup-plans-grid--pair-row"
                              : ""
                          }`}
                        >
                          {visiblePlans.map((plan) => (
                            <PlanCard
                              key={`${section.key}-${plan.id ?? planTypeBillingKey(plan)}`}
                              plan={plan}
                              sectionKey={section.key}
                              onSelect={handleSelectPlan}
                              isActive={isPlanActivatedForCard(
                                plan,
                                payload.activeSubscriptions
                              )}
                              activeSubscriptions={payload.activeSubscriptions}
                            />
                          ))}
                          {showDonation && visiblePlans.length > 0 ? (
                            <div className="subscription-pair-or">or</div>
                          ) : null}
                          {showDonation ? (
                            <DonationCard onDonate={handleStartDonation} />
                          ) : null}
                        </div>
                        {showDonation && hasActiveMemberPlan ? (
                          <div className="subscription-next-payment-row">
                            <p className="subscription-next-payment">
                              <span className="subscription-next-payment-line">
                                Next payment due,{" "}
                                <span className="subscription-emphasis">
                                  {memberNextPaymentText}
                                </span>
                              </span>
                            </p>
                          </div>
                        ) : null}
                      </section>
                    );
                  })}
                </div>
              ) : (
                <div
                  className="signup-plans-sections settings-subscription-plan-sections"
                  style={{ "--plans-columns": planColumnCount }}
                >
                  {planGroups.map((group) => {
                    /** Single lifetime card: sized to the "Introductory rates end…" line */
                    const isGaugedGroup =
                      group.key === "lifetime" &&
                      group.sections.length === 1 &&
                      group.sections[0].items.length === 1;
                    return (
                      <div
                        key={group.key}
                        className={`signup-plans-group signup-plans-group--${group.key}${
                          isGaugedGroup ? " signup-plans-group--gauged" : ""
                        }`}
                      >
                        <h2 className="signup-plans-group-title">{group.title}</h2>
                        {group.sections.map((section) => {
                          const showSectionTitle = !section.hideTitle && section.title;
                          const isBothSides = section.key === "both_sides";
                          const isGauged = isGaugedGroup && !isBothSides;
                          const cardEntries = section.items.map((plan) => ({
                            plan,
                            sectionKey: section.key,
                          }));
                          return (
                            <section
                              key={section.key}
                              className={`signup-plans-section${
                                isBothSides ? " signup-plans-section--both-sides" : ""
                              }`}
                            >
                              {showSectionTitle ? (
                                <h3 className="signup-plans-section-title">{section.title}</h3>
                              ) : null}
                              <div
                                className={`signup-plans-grid${
                                  isBothSides ? " signup-plans-grid--both-sides" : ""
                                }${isGauged ? " signup-plans-grid--gauged" : ""}`}
                              >
                                {isGauged ? (
                                  <span
                                    className="signup-plans-disclaimer signup-plans-card-width-gauge"
                                    aria-hidden="true"
                                  >
                                    {INTRO_RATE_END_TEXT}
                                  </span>
                                ) : null}
                                {cardEntries.map(({ plan, sectionKey }) => (
                                  <PlanCard
                                    key={`${sectionKey}-${plan.id ?? planTypeBillingKey(plan)}`}
                                    plan={plan}
                                    sectionKey={sectionKey}
                                    onSelect={handleSelectPlan}
                                    isActive={isPlanActivatedForCard(
                                      plan,
                                      payload.activeSubscriptions
                                    )}
                                    activeSubscriptions={payload.activeSubscriptions}
                                  />
                                ))}
                              </div>
                            </section>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManagement;
